package com.arena_web.analytics

import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.encodeToString

/**
 * First-touch UTM capture for the landing site.
 *
 * - Parses `utm_source/medium/campaign/content` from the current URL's query
 *   string and persists them to localStorage under key `"acq.utm"` together
 *   with a `savedAt` epoch-ms timestamp.
 * - First-touch semantics: an existing entry is kept if it is younger than
 *   30 days AND the current URL carries no UTM params.  When the URL has UTM
 *   params the entry is always overwritten (ad-link visit beats organic).
 * - Does NOT send any events itself: the initial `screen_view` comes from
 *   [AnalyticsTracker]'s route interceptor, whose batches already carry the
 *   captured UTM via `buildRequest()` and drain through `sendBeacon` on page
 *   hide. A separate beacon here produced a second session/device per visit
 *   (duplicate rows in the admin live feed) with a hardcoded desktop type.
 *
 * Call [init] once from `AppEntry` (or `initKobweb`) before any composition.
 */
object UtmCapture {

    private const val UTM_KEY = "acq.utm"
    private const val FBCLID_KEY = "acq.fbclid"
    private const val TTCLID_KEY = "acq.ttclid"
    private const val UTM_MAX_LEN = 200
    private const val THIRTY_DAYS_MS = 30L * 24L * 60L * 60L * 1000L

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
        explicitNulls = false
    }

    // Cached after the first [init] call so downstream callers can read without
    // re-parsing localStorage.
    private var cached: StoredUtm? = null
    private var initialized = false

    // ── Public API ──────────────────────────────────────────────────────────

    /**
     * Parse the current URL and persist UTM if present (or load a fresh-enough
     * stored value).  Idempotent — a second call is a no-op so hot-reload
     * remounts can't double-fire.
     */
    fun init() {
        if (initialized) return
        initialized = true
        runCatching {
            val parsed = parseCurrentUrl()
            cached = resolveUtm(parsed)
            captureFbclid()
            captureTtclid()
        }
    }

    /**
     * Returns the resolved UTM params (persisted first-touch or current URL),
     * or `null` for organic traffic.  Safe to call before [init] — returns
     * null in that case.
     */
    fun current(): StoredUtm? = cached

    /**
     * Builds a Google Play referrer query string suitable for appending to the
     * Play Store URL, e.g.:
     * ```
     * utm_source=meta&utm_medium=cpc&utm_campaign=summer&utm_content=banner
     * ```
     * Returns `null` when no UTM is stored (organic traffic — no decoration
     * needed).
     */
    fun playReferrer(): String? {
        val u = cached ?: return null
        return buildString {
            append("utm_source=").append(u.utmSource)
            u.utmMedium?.let { append("&utm_medium=").append(it) }
            u.utmCampaign?.let { append("&utm_campaign=").append(it) }
            u.utmContent?.let { append("&utm_content=").append(it) }
        }.takeIf { it.isNotEmpty() }
    }

    /** Persist ?fbclid from the current URL (first-touch overwrite on new visits). */
    private fun captureFbclid() {
        val search = runCatching { window.location.search }.getOrNull() ?: return
        if (search.length <= 1) return
        val params = runCatching { js("new URLSearchParams(search)").asDynamic() }.getOrNull() ?: return
        val fbclid = (params.get("fbclid") as? String)?.trim()?.take(UTM_MAX_LEN)?.takeIf { it.isNotEmpty() }
        if (fbclid != null) Storage.save(FBCLID_KEY, fbclid)
    }

    /** The persisted Facebook click id, or null. */
    fun fbclid(): String? = runCatching { Storage.get(FBCLID_KEY) }.getOrNull()?.takeIf { it.isNotEmpty() }

    /** The `_fbp` cookie value set by the base pixel, read client-side. */
    fun fbp(): String? = readCookie("_fbp")

    /** Persist ?ttclid from the current URL (first-touch overwrite on new visits). */
    private fun captureTtclid() {
        val search = runCatching { window.location.search }.getOrNull() ?: return
        if (search.length <= 1) return
        val params = runCatching { js("new URLSearchParams(search)").asDynamic() }.getOrNull() ?: return
        // ttclid values run longer than fbclid — allow up to 500 chars.
        val ttclid = (params.get("ttclid") as? String)?.trim()?.take(500)?.takeIf { it.isNotEmpty() }
        if (ttclid != null) Storage.save(TTCLID_KEY, ttclid)
    }

    /** The persisted TikTok click id, or null. */
    fun ttclid(): String? = runCatching { Storage.get(TTCLID_KEY) }.getOrNull()?.takeIf { it.isNotEmpty() }

    /** The `_ttp` cookie value set by the TikTok base pixel, read client-side. */
    fun ttp(): String? = readCookie("_ttp")

    /**
     * The analytics device id (same value the [AnalyticsTracker] batches carry).
     * Sent with signup so the backend can fall back to the most recent
     * `analytics_events` UTM for this device when no explicit UTM is in the body.
     */
    fun deviceId(): String? =
        runCatching { Storage.get(StorageKeys.ANALYTICS_DEVICE_ID) }.getOrNull()?.takeIf { it.isNotEmpty() }

    private fun readCookie(name: String): String? {
        val cookie = runCatching { kotlinx.browser.document.cookie }.getOrNull() ?: return null
        return cookie.split(";")
            .map { it.trim() }
            .firstOrNull { it.startsWith("$name=") }
            ?.substringAfter("=")
            ?.takeIf { it.isNotEmpty() }
    }

    // ── Internals ───────────────────────────────────────────────────────────

    /** Parse utm_* params from window.location.search. */
    private fun parseCurrentUrl(): RawUtm? {
        val search = runCatching { window.location.search }.getOrNull() ?: return null
        if (search.length <= 1) return null // empty or just "?"

        // URLSearchParams is available in all modern browsers (ES2015+).
        val params = runCatching {
            js("new URLSearchParams(search)").asDynamic()
        }.getOrNull() ?: return null

        fun get(name: String): String? =
            (params.get(name) as? String)?.trim()?.take(UTM_MAX_LEN)?.takeIf { it.isNotEmpty() }

        val source = get("utm_source") ?: return null   // source is mandatory; others optional
        return RawUtm(
            utmSource = source,
            utmMedium = get("utm_medium"),
            utmCampaign = get("utm_campaign"),
            utmContent = get("utm_content"),
        )
    }

    /**
     * Resolve which UTM to use:
     * - URL has UTM → overwrite storage, return that.
     * - URL is organic, stored is fresh (< 30 days) → return stored.
     * - URL is organic, stored is absent or stale → return null (organic).
     */
    private fun resolveUtm(parsed: RawUtm?): StoredUtm? {
        if (parsed != null) {
            // URL carries UTM — always overwrite.
            val stored = StoredUtm(
                utmSource = parsed.utmSource,
                utmMedium = parsed.utmMedium,
                utmCampaign = parsed.utmCampaign,
                utmContent = parsed.utmContent,
                savedAt = currentTimeMs(),
            )
            persistUtm(stored)
            return stored
        }

        // No UTM in URL — try to load a fresh stored value.
        val raw = Storage.get(UTM_KEY) ?: return null
        val stored = runCatching { json.decodeFromString<StoredUtm>(raw) }.getOrNull() ?: return null
        val age = currentTimeMs() - stored.savedAt
        if (age > THIRTY_DAYS_MS) {
            Storage.remove(UTM_KEY)
            return null
        }
        return stored
    }

    private fun persistUtm(utm: StoredUtm) {
        runCatching {
            Storage.save(UTM_KEY, json.encodeToString(utm))
        }
    }

    // ── Helpers ─────────────────────────────────────────────────────────────

    private fun currentTimeMs(): Long = kotlin.js.Date().getTime().toLong()
}

/** Persisted first-touch UTM attribution. */
@Serializable
data class StoredUtm(
    val utmSource: String,
    val utmMedium: String? = null,
    val utmCampaign: String? = null,
    val utmContent: String? = null,
    /** Epoch-ms when this entry was written. Used for 30-day expiry. */
    val savedAt: Long,
)

/** Raw params from the current URL — not yet persisted. */
private data class RawUtm(
    val utmSource: String,
    val utmMedium: String?,
    val utmCampaign: String?,
    val utmContent: String?,
)

/**
 * Shared wrapper around the browser's `encodeURIComponent` function.
 * Used by landing-page sections that append UTM referrer params to store URLs.
 */
fun encodeURIComponent(value: String): String =
    js("encodeURIComponent(value)") as String
